import React from 'react';
import { Building2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Chip, StatusDot, Surface, Text } from '@/components/flup';
import ScrollRail from './ScrollRail';

/**
 * Venue → sport → court, all on one line of sight.
 *
 * The rule the whole screen is built on: the vendor never *chooses* their way
 * to the board, they arrive on it. Venue, sport and court all default to the
 * first option, so the slots below are already the ones most vendors want; the
 * rails here exist only for the minority who run more than one of something.
 *
 * A single venue collapses to a plain heading rather than a rail of one — a
 * one-item picker is a decision the vendor doesn't have.
 */

export interface PickerOption {
  id: string;
  label: string;
  meta?: string;
  thumbnail?: string;
  live?: boolean;
}

const Rail: React.FC<{
  label: string;
  options: PickerOption[];
  value?: string;
  onChange: (id: string) => void;
  className?: string;
}> = ({ label, options, value, onChange, className }) => {
  if (!options.length) return null;

  return (
    <div className={cn('min-w-0', className)}>
      <Text variant="eyebrow" tone="tertiary" as="p" className="mb-2">
        {label}
      </Text>
      <ScrollRail className="gap-2">
        {options.map((option) => (
          <Chip
            key={option.id}
            label={option.label}
            selected={option.id === value}
            onClick={() => onChange(option.id)}
          />
        ))}
      </ScrollRail>
    </div>
  );
};

interface ContextPickerProps {
  venues: PickerOption[];
  venueId?: string;
  onVenueChange: (id: string) => void;

  sports: PickerOption[];
  sportId?: string;
  onSportChange: (id: string) => void;

  courts: PickerOption[];
  courtId?: string;
  onCourtChange: (id: string) => void;

  /**
   * Rendered under a hairline inside the same card. The date rail lives here
   * rather than in a card of its own: two cards cost a gap and two sets of
   * padding for one question the vendor answers in the same breath as "which
   * court".
   */
  footer?: React.ReactNode;
}

const ContextPicker: React.FC<ContextPickerProps> = ({
  venues,
  venueId,
  onVenueChange,
  sports,
  sportId,
  onSportChange,
  courts,
  courtId,
  onCourtChange,
  footer,
}) => {
  const activeVenue = venues.find((v) => v.id === venueId);
  const multiVenue = venues.length > 1;
  const hasTail = sports.length > 1 || courts.length > 0;

  return (
    <Surface padding="p-4 sm:p-5">
      {/*
        One band, not a stack. Everything here is context the vendor confirms at
        a glance and changes rarely — the work is the slot board underneath, and
        every row spent on the picker is a row of slots pushed off the screen.
        Below lg there isn't the width to be honest about it, so it stacks.
      */}
      <div
        className={cn(
          'flex flex-col gap-4 lg:gap-5',
          hasTail ? 'lg:grid lg:grid-cols-[65fr_35fr] lg:items-start' : null,
        )}
      >
        {multiVenue ? (
          <VenueRail
            venues={venues}
            venueId={venueId}
            onVenueChange={onVenueChange}
            className="min-w-0"
          />
        ) : activeVenue ? (
          <div className="flex items-center gap-2.5 min-w-0 lg:self-end lg:pb-1">
            <span className="grid place-items-center w-9 h-9 rounded-flup-sm bg-flup-surface-alt text-flup-ink shrink-0">
              <Building2 className="w-[18px] h-[18px]" aria-hidden />
            </span>
            <div className="min-w-0">
              <Text variant="h3" as="p" lines={1}>
                {activeVenue.label}
              </Text>
              <StatusDot
                status={activeVenue.live ? 'live' : 'unverified'}
                className="mt-0.5"
              />
            </div>
          </div>
        ) : null}

        {hasTail ? (
          <div className="flex gap-4 lg:gap-5 min-w-0">
            {sports.length > 1 ? (
              <Rail
                label="Sport"
                options={sports}
                value={sportId}
                onChange={onSportChange}
                className="flex-1"
              />
            ) : null}
            <Rail
              label="Court"
              options={courts}
              value={courtId}
              onChange={onCourtChange}
              className="flex-1"
            />
          </div>
        ) : null}
      </div>

      {footer ? (
        <>
          <div className="h-px bg-flup-hairline -mx-4 sm:-mx-5 mt-4 mb-4" />
          {footer}
        </>
      ) : null}
    </Surface>
  );
};

/**
 * Venue cards, not chips: with more than one venue the thumbnail is the fastest
 * way a vendor tells them apart, and the Live dot answers the question they ask
 * most often about a venue that isn't taking bookings.
 */
const VenueRail: React.FC<{
  venues: PickerOption[];
  venueId?: string;
  onVenueChange: (id: string) => void;
  className?: string;
}> = ({ venues, venueId, onVenueChange, className }) => (
  <div className={cn('min-w-0', className)}>
    <Text variant="eyebrow" tone="tertiary" as="p" className="mb-2">
      Venue · {venues.length}
    </Text>

    <ScrollRail className="gap-2.5">
      {venues.map((venue) => {
        const active = venue.id === venueId;
        return (
          <button
            key={venue.id}
            type="button"
            aria-pressed={active}
            onClick={() => onVenueChange(venue.id)}
            className={cn(
              'flex items-center gap-2.5 w-[204px] p-2 pr-3 rounded-flup-md border text-left transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-1',
              active
                ? 'bg-flup-green-soft border-flup-green'
                : 'bg-flup-surface border-flup-hairline hover:bg-flup-surface-alt hover:border-flup-hairline-2',
            )}
          >
            {venue.thumbnail ? (
              <img
                src={venue.thumbnail}
                alt=""
                className="w-10 h-10 rounded-flup-xs object-cover shrink-0 bg-flup-sunken"
              />
            ) : (
              <span className="grid place-items-center w-10 h-10 rounded-flup-xs bg-flup-sunken text-flup-ink-3 shrink-0">
                <MapPin className="w-4 h-4" aria-hidden />
              </span>
            )}

            <div className="min-w-0 flex-1">
              <Text
                variant="labelSemi"
                as="p"
                lines={1}
                className={active ? 'text-flup-green-ink' : 'text-flup-ink'}
              >
                {venue.label}
              </Text>
              <StatusDot
                status={venue.live ? 'live' : 'unverified'}
                label={venue.live ? 'Live' : venue.meta || 'Not live'}
                className="mt-0.5"
              />
            </div>
          </button>
        );
      })}
    </ScrollRail>
  </div>
);

export default React.memo(ContextPicker);
